import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Animated, Pressable, TextInput, View } from 'react-native'
import { Icon } from '@/components/ui/Icon'
import { Text } from '@/components/ui/Text'
import { useAuth } from '@/contexts/AuthContext'
import { useThemeColors } from '@/contexts/ThemeContext'
import { CommunityAvatar, getInitials } from './utils'

const MAX_CHARS = 2000
const WARN_THRESHOLD = 1800

type Props = {
  onSubmit: (body: string) => void
  loading: boolean
}

export const ComposeBox = forwardRef<{ focusAndExpand: () => void }, Props>(
  // eslint-disable-next-line complexity
  ({ onSubmit, loading }, ref) => {
    const { t } = useTranslation()
    const colors = useThemeColors()
    const { user } = useAuth()
    const [expanded, setExpanded] = useState(false)
    const [text, setText] = useState('')
    const [highlighted, setHighlighted] = useState(false)
    const inputRef = useRef<TextInput>(null)
    const borderWidthAnim = useRef(new Animated.Value(0)).current

    const inputStyle = { color: colors.dark, minHeight: 80 }

    const initials = user?.name?.trim() ? getInitials(user.name) : 'JP'

    const remaining = MAX_CHARS - text.length
    const showCounter = remaining < MAX_CHARS - WARN_THRESHOLD
    const canSubmit = text.trim().length > 0 && !loading && remaining >= 0

    useImperativeHandle(ref, () => ({
      focusAndExpand: () => {
        setExpanded(true)
        setHighlighted(true)
        Animated.timing(borderWidthAnim, {
          toValue: 2,
          duration: 300,
          useNativeDriver: false,
        }).start()
        setTimeout(() => {
          inputRef.current?.focus()
        }, 0)
      },
    }))

    function handleSubmit() {
      if (!canSubmit) return
      onSubmit(text.trim())
      setText('')
      setExpanded(false)
    }

    if (!expanded) {
      return (
        <Pressable
          testID="community-compose-open"
          className="rounded-2xl px-5 py-4 flex-row items-center gap-3"
          style={{ backgroundColor: colors.surface }}
          onPress={() => setExpanded(true)}
        >
          <CommunityAvatar initials={initials} />
          <Text className="flex-1 text-sm" style={{ color: colors.muted }}>
            {t('communityFeed.composePlaceholder')}
          </Text>
        </Pressable>
      )
    }

    return (
      <View className="rounded-2xl p-2 sm:p-4 gap-3" style={{ backgroundColor: colors.surface }}>
        <View className="flex-row items-start gap-3">
          <View className="hidden sm:block">
            <CommunityAvatar initials={initials} />
          </View>
          <Animated.View
            style={{
              flex: 1,
              borderWidth: borderWidthAnim,
              borderColor: highlighted ? colors.primary : colors.border,
              borderRadius: 8,
            }}
          >
            <TextInput
              testID="community-compose-input"
              ref={inputRef}
              className="text-sm px-0 sm:px-2 py-2"
              style={inputStyle}
              placeholder={t('communityFeed.composePlaceholder')}
              placeholderTextColor={colors.muted}
              value={text}
              onChangeText={setText}
              multiline
              autoFocus
              maxLength={MAX_CHARS}
              onBlur={() => {
                setHighlighted(false)
                Animated.timing(borderWidthAnim, {
                  toValue: 0,
                  duration: 300,
                  useNativeDriver: false,
                }).start()
              }}
            />
          </Animated.View>
        </View>
        <View className="flex-row items-center justify-between">
          <View className="flex-row items-center gap-2 sm:hidden">
            <CommunityAvatar initials={initials} />
            {showCounter && (
              <Text className="text-xs" style={{ color: remaining < 0 ? '#e53e3e' : colors.muted }}>
                {t('communityFeed.charLimit', { count: remaining })}
              </Text>
            )}
          </View>
          <View className="hidden sm:block">
            {showCounter && (
              <Text className="text-xs" style={{ color: remaining < 0 ? '#e53e3e' : colors.muted }}>
                {t('communityFeed.charLimit', { count: remaining })}
              </Text>
            )}
          </View>
          <View className="flex-row gap-2">
            <Pressable
              className="px-4 py-2 rounded-full"
              style={{ backgroundColor: colors.border }}
              onPress={() => {
                setExpanded(false)
                setText('')
              }}
            >
              <Icon name="times" size={12} color={colors.dark} />
            </Pressable>
            <Pressable
              testID="community-compose-submit"
              className="px-4 py-2 rounded-full"
              style={{ backgroundColor: canSubmit ? colors.primary : colors.border }}
              onPress={handleSubmit}
              disabled={!canSubmit}
            >
              <Text
                className="text-xs font-medium"
                style={{ color: canSubmit ? colors.white : colors.muted }}
              >
                {t('communityFeed.postButton')}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    )
  }
)

ComposeBox.displayName = 'ComposeBox'
